/**
 * PageStatusBootstrapper — runs once at app mount, fetches the page
 * status table, and writes it to the zustand store. Refetches every
 * 60s so a page going under maintenance is reflected without a reload.
 *
 * Mounted high in the React tree (above the router) so the gate can
 * use it synchronously.
 */

import { useEffect } from 'react'
import { usePageStatusStore } from './store'
import type { PublicPageStatus } from './types'

const API_URL = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '')
const REFRESH_INTERVAL_MS = 60_000

interface Envelope<T> {
  success: boolean
  data?: T
  error?: { code: string; message: string }
}

async function fetchStatuses(): Promise<PublicPageStatus[]> {
  if (!API_URL) return []
  const res = await fetch(`${API_URL}/page-statuses`, {
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  const json = (await res.json()) as Envelope<PublicPageStatus[]> | PublicPageStatus[]
  if (Array.isArray(json)) return json
  if (!json.success) throw new Error(json.error?.message ?? 'Failed')
  return json.data ?? []
}

export function PageStatusBootstrapper() {
  const setList = usePageStatusStore((s) => s.setList)
  const setError = usePageStatusStore((s) => s.setError)

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      try {
        const data = await fetchStatuses()
        if (!cancelled) setList(data)
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err))
        }
      }
    }

    // Defer the first fetch off the critical render path. Page-status
    // only gates a handful of routes, so it can wait until the browser
    // is idle (or 1.5s max) — this keeps the main thread free during
    // the initial paint and cuts Total Blocking Time on mobile.
    const ric =
      typeof window !== 'undefined' &&
      typeof window.requestIdleCallback === 'function'
        ? window.requestIdleCallback
        : (cb: () => void, _opts?: { timeout?: number }) =>
            window.setTimeout(cb, 1)
    ric(() => void load(), { timeout: 1500 })

    const interval = window.setInterval(() => void load(), REFRESH_INTERVAL_MS)
    return () => {
      cancelled = true
      window.clearInterval(interval)
    }
  }, [setError, setList])

  return null
}
